import { useEffect, useMemo } from "react";
import { FieldValues, Path, useFormContext, useWatch } from "react-hook-form";
import { SxProps } from "@mui/material";
import { SelectInput } from "./SelectInput";
import {
  DrivingLicenseClass,
  isForCarOnly,
  isForCarWithTrailer,
  isForMotorcycle,
  isForTractor,
} from "../model/DrivingLicenseClass";

export function DrivingLessonTypeInput(props: {
  source: Path<FieldValues>;
  bulkEdit?: boolean;
  size?: "small";
  fullWidth?: boolean;
  sx?: SxProps;
}) {
  const { source, bulkEdit, size, fullWidth, sx } = props;
  const drivingLicenseClass = useWatch<{ drivingLicenseClass: DrivingLicenseClass | "__DO_NOT_CHANGE__" }>({
    name: "drivingLicenseClass",
  });
  const { getValues, setValue } = useFormContext();
  const options = useMemo(() => _getDrivingLessonTypeOptions(drivingLicenseClass), [drivingLicenseClass]);
  // If the current selection is no longer a valid option, we reset the selection ...
  useEffect(() => {
    if (options.length > 0) {
      const selection = getValues(source);
      if (bulkEdit && selection === "__DO_NOT_CHANGE__") {
        return;
      }
      if (selection && !options.map((option) => option[0]).includes(selection)) {
        setValue(source, "");
      }
    }
  }, [options, source, bulkEdit]);

  return (
    <SelectInput
      source={source}
      bulkEdit={bulkEdit}
      label="Art"
      options={options}
      validate={(value) => {
        if (value && options.some((option) => option[0] === value)) {
          return true;
        }
        if (bulkEdit && value === "__DO_NOT_CHANGE__") {
          return true;
        }
        return "Bitte wähle die Art der Fahrstunde aus.";
      }}
      size={size}
      fullWidth={fullWidth}
      sx={sx}
    />
  );
}

function _getDrivingLessonTypeOptions(
  drivingLicenseClass?: DrivingLicenseClass | "__DO_NOT_CHANGE__",
): Array<[string, string]> {
  if (!drivingLicenseClass) {
    return [];
  }
  const options: Array<[string, string]> = [
    ["normal", "Übungsfahrt"],
    ["autobahnfahrt", "Autobahnfahrt"],
    ["nachtfahrt", "Nachtfahrt"],
    ["ueberlandfahrt", "Überlandfahrt"],
  ];
  if (drivingLicenseClass === "__DO_NOT_CHANGE__") {
    return options;
  }
  if (isForCarOnly(drivingLicenseClass) || isForMotorcycle(drivingLicenseClass) || isForTractor(drivingLicenseClass)) {
    options.push(["unterweisungAmFahrzeug", "Unterweisung am Fahrzeug"]);
  }
  if (isForCarWithTrailer(drivingLicenseClass)) {
    options.push(["praktischeUnterweisung", "Praktische Unterweisung"]);
  }
  if (drivingLicenseClass === "B197") {
    options.push(["schaltkompetenz", "Testfahrt Schaltkompetenz"]);
  }
  return options;
}
